import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, CheckCircle, Package, Users, Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to format relative date
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const DeliveredOrders = () => {
  const { getAuthHeaders, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const isDepot = user?.role === 'depot';

  const fetchDeliveredOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/orders/delivered`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching delivered orders:', error);
      setError('Unable to load delivered orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveredOrders();
  }, []);

  // Filter orders based on search
  const filteredOrders = orders.filter(order => {
    const searchLower = search.toLowerCase();
    return (
      order.item_name?.toLowerCase().includes(searchLower) ||
      order.sku?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate stats
  const totalDelivered = orders.length;
  const totalQuantity = orders.reduce((sum, o) => sum + (o.total_quantity || 0), 0);
  const uniqueItems = new Set(orders.map(o => o.item_name)).size;

  if (loading) return <LoadingOverlay message="Loading delivered orders..." />;
  if (error) return <ErrorState message={error} onRetry={fetchDeliveredOrders} />;

  return (
    <div className="space-y-6" data-testid="delivered-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Delivered Orders</h1>
          <p className="text-slate-500 mt-1">
            {isDepot ? 'Orders you contributed to that have been delivered' : 'All completed deliveries'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Delivered</p>
              <p className="text-2xl font-bold text-slate-900">{totalDelivered}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Quantity</p>
              <p className="text-2xl font-bold text-slate-900">{totalQuantity.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unique Items</p>
              <p className="text-2xl font-bold text-slate-900">{uniqueItems}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Search by item name or SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="delivered-orders-search"
        />
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No delivered orders</h3>
            <p className="text-slate-500">
              {search ? 'No orders match your search.' : 'Delivered orders will appear here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredOrders.map((order) => (
            <Card key={order.id} className="hover:shadow-md transition-shadow" data-testid={`delivered-order-${order.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Item Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{order.item_name}</h3>
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Delivered
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500">SKU: {order.sku || 'N/A'}</p>
                  </div>

                  {/* Quantity */}
                  <div className="text-center px-4">
                    <p className="text-2xl font-bold text-slate-900">{order.total_quantity}</p>
                    <p className="text-xs text-slate-500">units delivered</p>
                  </div>

                  {/* Contributors */}
                  <div className="flex items-center gap-2 px-4">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      {(order.add_ons?.length || 0) + 1} contributor{(order.add_ons?.length || 0) > 0 ? 's' : ''}
                    </span>
                  </div>

                  {/* Delivery Date */}
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-green-600">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium">{formatRelativeDate(order.updated_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{formatDate(order.updated_at)}</p>
                  </div>
                </div>

                {/* Contribution Details (Expandable) */}
                {order.add_ons && order.add_ons.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2">CONTRIBUTIONS</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs">
                        {order.created_by_name || order.created_by_depot_name || 'Creator'}: {order.quantity || 0} units
                      </Badge>
                      {order.add_ons.map((addon, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {addon.depot_name || addon.added_by_name || `Depot`}: {addon.quantity} units
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliveredOrders;
